/**
 * デイリーボーナス API
 *
 * Utage-Web の `dailyBonusService.getInitialData()` に倣い、ページ表示時に
 * 1 フェッチで 2 API を並列コールして集約する:
 *   - `utage_web_daily_bonus`           … 受取（claim）。サーバ側で日付ベースに idempotent。
 *                                          本日初回のみ `add_point > 0`、受取済みなら 0。
 *   - `get_utage_web_point_info`        … ポイント残高＋Premium 購入有無
 *
 * 「本日もう受け取ったか」はサーバ応答の `add_point` で判定する。
 * 受取の有無はサーバが per-user/per-day で管理するため、ページに来るたび claim を叩いてよい。
 * loader の `fetchQuery` で毎訪問フレッシュに取得し、`isAddPoint = add_point > 0` を導出する。
 */

import { queryOptions } from "@tanstack/react-query";
import {
  GET_UTAGE_WEB_POINT_INFO,
  UTAGE_WEB_DAILY_BONUS,
} from "~/constants/apiEndpoints";
import { apiClient } from "~/lib/apiClient";
import { queryKeys } from "~/lib/queryKeys";
import type {
  DailyBonusData,
  GetUtageWebPointInfoResponse,
  UtageWebDailyBonusResponse,
} from "./types";

/**
 * デイリーボーナス取得（受取 + 残高を 1 フェッチで集約）
 *
 * claim を含むため、ページに来るたびフレッシュに叩く（`staleTime: 0`）。
 * loader 側は `fetchQuery` で取得し、`refetchOnMount: false` でマウント時の二重 claim を防ぐ。
 */
export const dailyBonusQueryOptions = queryOptions({
  queryKey: queryKeys.dailyBonus.data(),
  queryFn: async (): Promise<DailyBonusData> => {
    const [bonus, pointInfo] = await Promise.all([
      apiClient<UtageWebDailyBonusResponse>(UTAGE_WEB_DAILY_BONUS),
      apiClient<GetUtageWebPointInfoResponse>(GET_UTAGE_WEB_POINT_INFO),
    ]);

    const addPoint = bonus.add_point ?? 0;

    return {
      // 本日初回受取のみ add_point > 0。サーバ応答が唯一の判定根拠。
      isAddPoint: addPoint > 0,
      addPoint,
      doubleDailyBonusCount: bonus.double_daily_bonus_days,
      doubleBonusLimitDate: bonus.double_bonus_limit_date,
      isPurchased: !!pointInfo.is_purchased,
    };
  },
  // claim を含むので毎回フレッシュに取得する。loader(fetchQuery) が再訪のたび再フェッチする。
  staleTime: 0,
  // loader が取得済みのデータをマウント時に再フェッチしない（= 二重 claim を防ぐ）
  refetchOnMount: false,
  refetchOnWindowFocus: false,
});
